import { LoaderTransparent } from 'components/molecules/LoaderTransparent/LoaderTransparent'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getTemplateDetail, updateTemplateStatus } from 'services/templateService'
import { RootState } from 'store/store'
import HeaderPageSection from 'core/presentation/components/molecules/HeaderPageSection/HeaderPageSection'
import HeaderPatternDetail from 'templates/presentation/components/molecules/HeaderPatternDetail/HeaderPatternDetail'
import AppBox from 'components/atoms/AppBox/AppBox'
import { AccordionPattern } from 'components/molecules/AccordionPattern/AccordionPattern'
import moment from 'moment'
import { Criteria } from 'interfaces/patternDetails'
import ImageEmptyList from 'components/molecules/ImageEmptyList/ImageEmptyList'
import EmptyBox from 'assets/empty-box.png'
import { useNavigate } from 'react-router-dom'
import Layout from 'layout/Layout'
import AppButton from 'components/atoms/AppButton/AppButton'
import StatusToast from 'components/molecules/StatusToast/StatusToast'
import { usePermissions } from 'hooks/usePermissions'
import { RolePermissions } from 'utils/constants'
import { OperationMode } from 'core/domain/operationMode'
import { OperationModeBadge } from 'core/presentation/components/molecules/OperationModeBadge/OperationModeBadge'
import { TEMPLATE_STATUS, TemplateStatus, isDeactivated, normalizeTemplateStatus } from 'templates/domain/entities/TemplateStatus'
import { TemplateStatusBadge } from 'templates/presentation/components/molecules/TemplateStatusBadge'
import { getApiErrorMessage } from 'utils/apiError'

interface Props {
    pattern: number
}

export default function PatternRulesList({ pattern }: Props) {
    const navigate = useNavigate()
    const { apiToken } = useSelector((state: RootState) => state.auth)
    const [loading, setLoading] = useState(true)
    const { can } = usePermissions()

    interface RuleGroup {
        id: number
        name: string
        chutes: number[]
        priority?: number | null
        sequence?: number | null
        criterias: Criteria[]
        providerService?: {
            code: string
            name: string
        } | null
        change_route?: boolean
    }

    interface RulesList {
        id: number | null
        createdAt: string
        updatedAt: string
        chutesAssigned: number[]
        chutesNotAssigned: number[]
        name: string
        groups: RuleGroup[]
        operationMode?: OperationMode
        status?: TemplateStatus
        statusName?: string
        hasActiveOrders: boolean
    }

    const [rulesList, setRulesList] = useState<RulesList>({
        id: null,
        createdAt: '',
        updatedAt: '',
        chutesAssigned: [],
        chutesNotAssigned: [],
        name: '',
        groups: [],
        operationMode: undefined,
        status: undefined,
        statusName: undefined,
        hasActiveOrders: false,
    })

    const isClassification = rulesList.operationMode?.code === 'classification'

    const [toast, setToast] = useState<{ title: string; description?: string; type: 'success' | 'error' | 'loading'; visible: boolean }>({
        title: '',
        description: undefined,
        type: 'success',
        visible: false,
    })
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

    const showToast = (title: string, type: 'success' | 'error' | 'loading', description?: string) => {
        setToast({ title, description, type, visible: true })
        setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000)
    }

    const handleStatusChange = async (active: boolean) => {
        if (rulesList.id === null || isUpdatingStatus) return

        setIsUpdatingStatus(true)
        try {
            const response = await updateTemplateStatus(
                rulesList.id,
                active ? 'active' : 'deactivated'
            )
            setRulesList(prev => ({
                ...prev,
                status:
                    normalizeTemplateStatus(response?.status?.key) ??
                    (active ? TEMPLATE_STATUS.ACTIVE : TEMPLATE_STATUS.DEACTIVATED),
                statusName: response?.status?.name,
            }))
            if (active) {
                showToast('¡Plantilla activada con éxito!', 'success')
            } else {
                showToast(
                    'Plantilla desactivada',
                    'success',
                    'No podrás asignar paquetes a esta plantilla'
                )
            }
        } catch (error) {
            showToast(
                getApiErrorMessage(error, 'Error al actualizar el estado de la plantilla'),
                'error'
            )
        } finally {
            setIsUpdatingStatus(false)
        }
    }

    const breadcrumbNavigate = [
        {
            label: 'Plantillas',
            path: '/templates',
        },
        {
            label: 'Detalle de plantilla',
            path: '/packages/create',
        },
    ]

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const data = await getTemplateDetail(pattern)
                if (data !== undefined && data !== null) {
                    const sortedGroups = (data.groups ?? [])
                        .slice()
                        .sort((a: RuleGroup, b: RuleGroup) => (a.priority ?? 0) - (b.priority ?? 0))
                    setRulesList({
                        id: data.id,
                        name: data.name,
                        createdAt: data.created_at,
                        updatedAt: data.updated_at,
                        chutesAssigned: data.chutes_assigned ?? [],
                        chutesNotAssigned: data.chutes_not_assigned ?? [],
                        groups: sortedGroups,
                        status: normalizeTemplateStatus(data.status?.key),
                        statusName: data.status?.name,
                        hasActiveOrders: data.has_active_orders ?? false,
                        operationMode: data.classificationType
                            ? {
                                  id: 0,
                                  code: data.classificationType.key,
                                  name: data.classificationType.description,
                              }
                            : undefined,
                    })
                }
                setLoading(false)
            } catch (error) {
                setLoading(false)
            }
        }

        fetchData()
    }, [apiToken])

    return (
        <Layout>
            <HeaderPageSection
                title={rulesList.name}
                showGoBack={false}
                titleBadgeGap="tight"
                breadcrumbItems={breadcrumbNavigate}
                badgeNode={
                    <div className="flex items-center gap-1">
                        <TemplateStatusBadge status={rulesList.status} label={rulesList.statusName} />
                        {rulesList.operationMode && (
                            <OperationModeBadge
                                operationMode={rulesList.operationMode}
                            />
                        )}
                    </div>
                }
            >
                {(isClassification
                    ? can(RolePermissions.templates.edit)
                    : can(RolePermissions.templates.chutes.assign) && rulesList.status !== TEMPLATE_STATUS.ACTIVE) && (
                    <AppButton
                        title={isClassification ? 'Agregar reglas' : 'Asignar chutes'}
                        state="primary"
                        size="sm"
                        disabled={isDeactivated(rulesList.status)}
                        className="drop-shadow-[0px_3px_3px_rgba(148,163,184,0.15)] disabled:drop-shadow-none"
                        onClick={() =>
                            navigate(`/templates/edit/${pattern}`, {
                                state: { mode: isClassification ? 'add-rules' : 'assign-chutes' },
                            })
                        }
                    />
                )}
            </HeaderPageSection>
            <>
                {(loading || isUpdatingStatus) && <LoaderTransparent />}
                <HeaderPatternDetail
                            created={{
                                createdAt: moment(rulesList.createdAt).format('DD/MM/YYYY'),
                                hour: moment(rulesList.createdAt).format('HH:mm'),
                            }}
                            updatedAt={
                                rulesList.updatedAt
                                    ? {
                                          createdAt: moment(rulesList.updatedAt).format('DD/MM/YYYY'),
                                          hour: moment(rulesList.updatedAt).format('HH:mm'),
                                      }
                                    : undefined
                            }
                            status={rulesList.status}
                            countRules={rulesList.groups.length}
                            chutesAssigned={
                                Array.isArray(rulesList.chutesAssigned)
                                    ? {
                                        totals: rulesList.chutesAssigned.length,
                                        notProcessed: Array.isArray(rulesList.chutesNotAssigned)
                                            ? rulesList.chutesNotAssigned.length
                                            : 0,
                                    }
                                    : { totals: 0, notProcessed: 0 }
                            }
                            isClassification={isClassification}
                            onStatusChange={handleStatusChange}
                            updatingStatus={isUpdatingStatus}
                            hasActiveOrders={rulesList.hasActiveOrders}
                        />
                        <AppBox>
                            <div className="bg-[#F1F5F9] px-[12px] py-2 flex justify-between items-center rounded-xl">
                                <p className="text-sm font-normal text-[#141A1F] w-2/3">
                                    Nombre de regla
                                </p>
                                {!isClassification && (
                                    <p className="text-sm font-normal text-[#141A1F] w-2/3 text-end">
                                        Chutes asignados
                                    </p>
                                )}
                            </div>
                            <div
                                className="overflow-y-auto"
                                style={{ height: '500px' }}
                            >
                                {rulesList.groups.length > 0 ? (
                                    <div className="flex justify-center py-3 text-sm">
                                        <AccordionPattern
                                            rules={rulesList.groups}
                                            isClassification={isClassification}
                                        />
                                    </div>
                                ) : (
                                    <div className="flex justify-center p-4 text-sm">
                                        <ImageEmptyList
                                            image={EmptyBox}
                                            title="Aún no has agregado ninguna regla"
                                            description="Asigna una o más reglas para esta plantilla"
                                            textButton="Agregar reglas"
                                            onClick={() =>
                                                navigate(`/templates/edit/${pattern}`, {
                                                    state: { mode: isClassification ? 'add-rules' : 'assign-chutes' },
                                                })
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </AppBox>
            </>
            {toast.visible && <StatusToast title={toast.title} description={toast.description} type={toast.type} />}
        </Layout>
    )
}
